import React, { useState } from 'react';
import { Modal, Form, Select, Input, message } from 'antd';
import {
  FileTextOutlined,
  PlusOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import { Button } from 'antd';
import { createReportTemplate, updateReportTemplate } from '../../services/regulatoryReportingService';

const { Option } = Select;
const { TextArea } = Input;

const REPORT_TYPES = [
  { value: 'CBN_RETURNS', label: 'CBN Returns' },
  { value: 'NDIC_RETURNS', label: 'NDIC Returns' },
  { value: 'AML_CTR', label: 'AML — Currency Transaction Report (CTR)' },
  { value: 'AML_SAR', label: 'AML — Suspicious Activity Report (SAR)' },
  { value: 'AML_STR', label: 'AML — Suspicious Transaction Report (STR)' },
  { value: 'NFIU_REPORT', label: 'NFIU Report' },
  { value: 'EFCC_REPORT', label: 'EFCC Compliance Report' },
  { value: 'CREDIT_BUREAU', label: 'Credit Bureau Report' },
  { value: 'CAPITAL_ADEQUACY', label: 'Capital Adequacy Report' },
  { value: 'LIQUIDITY_REPORT', label: 'Liquidity Report' },
  { value: 'PRUDENTIAL_RETURNS', label: 'Prudential Returns' },
  { value: 'RISK_ASSESSMENT', label: 'Risk Assessment Report' },
  { value: 'KYC_COMPLIANCE', label: 'KYC Compliance Report' },
  { value: 'TRANSACTION_MONITORING', label: 'Transaction Monitoring Report' },
  { value: 'CUSTOM', label: 'Custom Report' },
];

const TemplateFormModal = ({ visible, onClose, onSuccess, editRecord = null }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const isEditing = !!editRecord;

  React.useEffect(() => {
    if (editRecord && visible) {
      form.setFieldsValue({
        templateName: editRecord.templateName,
        reportType: editRecord.reportType,
        description: editRecord.description,
        fieldMappings: editRecord.fieldMappings || [{ sourceField: '', targetField: '' }],
      });
    } else if (!visible) {
      form.resetFields();
    }
  }, [editRecord, visible, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload = {
        templateName: values.templateName,
        reportType: values.reportType,
        description: values.description || '',
        fieldMappings: (values.fieldMappings || []).filter(
          (m) => m.sourceField && m.targetField
        ),
      };

      let res;
      if (isEditing) {
        res = await updateReportTemplate(editRecord.id, payload);
      } else {
        res = await createReportTemplate(payload);
      }

      if (res?.status) {
        message.success(`Template ${isEditing ? 'updated' : 'created'} successfully`);
        form.resetFields();
        onSuccess?.();
        onClose();
      } else {
        message.error(res?.message || `Failed to ${isEditing ? 'update' : 'create'} template`);
      }
    } catch (err) {
      if (err?.errorFields) return;
      console.error('Template error:', err);
      message.error('An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#fff' }}>
          <FileTextOutlined style={{ color: '#ff6700' }} />
          {isEditing ? 'Edit Template' : 'Create Report Template'}
        </span>
      }
      open={visible}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={isEditing ? 'Update Template' : 'Create Template'}
      okButtonProps={{
        loading,
        style: {
          background: '#ff6700',
          borderColor: '#ff6700',
          borderRadius: 8,
          fontWeight: 700,
        },
      }}
      cancelButtonProps={{ style: { borderRadius: 8 } }}
      width={680}
      className="reg-form-modal"
      centered
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark="optional"
        initialValues={{ fieldMappings: [{ sourceField: '', targetField: '' }] }}
      >
        <Form.Item
          name="templateName"
          label="Template Name"
          rules={[{ required: true, message: 'Please enter a template name' }]}
        >
          <Input placeholder="e.g., CBN Monthly Returns Template" size="large" />
        </Form.Item>

        <Form.Item
          name="reportType"
          label="Report Type"
          rules={[{ required: true, message: 'Please select a report type' }]}
        >
          <Select
            placeholder="Select report type"
            size="large"
            showSearch
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {REPORT_TYPES.map((rt) => (
              <Option key={rt.value} value={rt.value}>
                {rt.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="description" label="Description (Optional)">
          <TextArea
            rows={2}
            placeholder="Describe the purpose of this template..."
            maxLength={300}
            showCount
          />
        </Form.Item>

        {/* ── Field Mappings ──────────────── */}
        <div style={{ marginBottom: 8 }}>
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: '#595959',
              textTransform: 'uppercase',
              letterSpacing: '0.4px',
            }}
          >
            Field Mappings
          </span>
        </div>
        <div className="reg-field-map-container">
          <Form.List name="fieldMappings">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div key={key} className="reg-field-map-row">
                    <Form.Item
                      {...restField}
                      name={[name, 'sourceField']}
                      style={{ flex: 1, marginBottom: 0 }}
                    >
                      <Input placeholder="Source Field (system)" />
                    </Form.Item>
                    <span style={{ color: '#8c8c8c', fontWeight: 600 }}>→</span>
                    <Form.Item
                      {...restField}
                      name={[name, 'targetField']}
                      style={{ flex: 1, marginBottom: 0 }}
                    >
                      <Input placeholder="Target Field (report)" />
                    </Form.Item>
                    {fields.length > 1 && (
                      <MinusCircleOutlined
                        onClick={() => remove(name)}
                        style={{ color: '#ff4d4f', fontSize: 16, cursor: 'pointer' }}
                      />
                    )}
                  </div>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add()}
                  block
                  icon={<PlusOutlined />}
                  style={{ marginTop: 8, borderRadius: 8 }}
                >
                  Add Field Mapping
                </Button>
              </>
            )}
          </Form.List>
        </div>
      </Form>
    </Modal>
  );
};

export default TemplateFormModal;
